import { Box, Chip, Typography } from "@material-ui/core";
import { Button, CircularProgress, Divider, LinearProgress } from "@mui/material";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import ModalComponent from "../../criar-vitrine/incluir-produtos/components/modal";

/**
 * Modal de seleção em massa reutilizável — mesmo conceito em todo o B2B (produtos, clientes,
 * representantes): 4 ações condicionais de escopo (selecionar/desselecionar página ou tudo) mais
 * cancelar, com uma ação de confirmação opcional (ex.: "Remover") pra fluxos de exclusão.
 *
 * Visibilidade das ações de escopo, sempre reavaliada a cada abertura/mudança de estado:
 * - Selecionar página: página atual não tem nenhum item selecionado.
 * - Selecionar todos: nem todos os itens possíveis estão selecionados.
 * - Desselecionar página: página atual está 100% selecionada.
 * - Desselecionar todos: há itens selecionados além da página atual.
 */
export default function BulkSelectionModal({
  open,
  onClose,
  title = "Selecionar itens",
  entityLabel = "itens",
  pageCount = 0,
  pageSelectedCount = 0,
  totalCount = 0,
  totalSelectedCount = 0,
  loading = false,
  onSelectPage,
  onSelectAll,
  onDeselectPage,
  onDeselectAll,
  confirmAction,
}) {
  const showSelectPage = pageCount > 0 && pageSelectedCount === 0;
  const showSelectAll = totalSelectedCount < totalCount;
  const showDeselectPage = pageCount > 0 && pageSelectedCount === pageCount;
  const showDeselectAll = totalSelectedCount > pageCount;

  const closesOnScopeAction = !confirmAction;

  const run = (action) => async () => {
    await action?.();
    if (closesOnScopeAction) onClose?.();
  };

  const progress =
    totalCount > 0 ? Math.min(100, (totalSelectedCount / totalCount) * 100) : 0;

  return (
    <ModalComponent open={open} onClose={loading ? undefined : onClose} width="480px" title={title}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="body2" style={{ color: themeColorsDesingSystem.gray.dark }}>
          Seleção atual
        </Typography>
        <Chip
          size="small"
          label={`${totalSelectedCount} de ${totalCount} ${entityLabel}`}
          style={{
            backgroundColor: themeColorsDesingSystem.secondary.main + "40",
            color: themeColorsDesingSystem.primary.main,
            fontWeight: 700,
          }}
        />
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 4,
          backgroundColor: themeColorsDesingSystem.stone.light,
          "& .MuiLinearProgress-bar": {
            backgroundColor: themeColorsDesingSystem.primary.main,
            borderRadius: 4,
          },
        }}
      />
      <Typography
        variant="caption"
        style={{ color: themeColorsDesingSystem.gray.dark, marginTop: 4, display: "block" }}
      >
        Nesta página: {pageSelectedCount} de {pageCount} {entityLabel}
      </Typography>

      <Divider style={{ margin: "16px 0" }} />

      <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
        {showSelectPage && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<LibraryAddCheckIcon fontSize="small" />}
            disabled={loading}
            onClick={run(onSelectPage)}
          >
            Selecionar página
          </Button>
        )}
        {showSelectAll && (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DoneAllIcon fontSize="small" />
              )
            }
            disabled={loading}
            onClick={run(onSelectAll)}
          >
            Selecionar todos
          </Button>
        )}
        {showDeselectPage && (
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<PlaylistRemoveIcon fontSize="small" />}
            disabled={loading}
            onClick={run(onDeselectPage)}
          >
            Desselecionar página
          </Button>
        )}
        {showDeselectAll && (
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<RemoveDoneIcon fontSize="small" />}
            disabled={loading}
            onClick={run(onDeselectAll)}
          >
            Desselecionar todos
          </Button>
        )}
      </Box>

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        style={{ gap: 12, marginTop: 24 }}
      >
        <Button variant="text" disabled={loading} onClick={onClose}>
          Cancelar
        </Button>
        {confirmAction && (
          <Button
            variant="contained"
            color={confirmAction.color ?? "error"}
            startIcon={confirmAction.icon ?? <DeleteOutlineIcon fontSize="small" />}
            disabled={loading || confirmAction.disabled}
            onClick={confirmAction.onClick}
          >
            {confirmAction.label}
          </Button>
        )}
      </Box>
    </ModalComponent>
  );
}
